<template>
    <div class="responsive-image" :style="style"
         :class="{'responsive-image--loaded': lazyLoaded, 'responsive-image--lowsrc': lowsrc}">
        <img ref="imageRef" v-on-error :src="src" :alt="alt"
             :srcset="srcset" :sizes="sizesFromWidthOnScreen" v-bind="$attrs" @load="handleLoad">
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, StyleValue } from 'vue';
import keys from 'lodash-es/keys';
import map from 'lodash-es/map';
import sortBy from 'lodash-es/sortBy';
import zipObject from 'lodash-es/zipObject';
import breakpointService from '@/core/responsive/breakpoints/breakpoints.service';
import loggingService from '@/core/logging.service';
import breakpointsState from '@/core/responsive/breakpoints/breakpointsState.observable';
import responsiveImageService from '@/core/responsive/image/responsiveImage.service';
import { IResponsiveImageWidthOnScreenConfig, IFocalPoint } from '@/core/responsive/image/responsiveImage.types';

interface ResponsiveImageProps {
    imageUrl?: string;
    widthOnScreen?: IResponsiveImageWidthOnScreenConfig | number;
    /* Can take a single aspect ratio number, or an object with a number pr. breakpoint. If a 'default' is provided is will be used when no breakpoint is active:
        {
             xs: 16/9,
             min-sm,max-md: 4/3
             default: 1/2
        }
    */
    aspectRatio?: number | { [breakpoint: string]: number };
    offset?: number;
    lowsrc?: boolean;
    bgColor?: string;
    quality?: number;
    alt?: string;
    focalPoint?: IFocalPoint | null;
    mode?: 'crop' | 'pad' | 'max';
    // JPEG is replaced with JPG and treated as JPG, and is only here to support umbraco exposes JPEG if that is the file extension uploaded.
    format?: 'jpg' | 'jpeg' | 'png' | 'gif' | 'webp';
}
const props = withDefaults(defineProps<ResponsiveImageProps>(), {
    imageUrl: '',
    widthOnScreen: 100,
    aspectRatio: 16 / 9,
    offset: 200,
    lowsrc: false,
    bgColor: 'transparent',
    quality: 75,
    alt: '',
    focalPoint: null,
    mode: 'crop',
    format: 'jpg'
});

const emit = defineEmits({
    loaded: () => true
});

const imageRef = ref<HTMLImageElement | null>(null);
const sizes = responsiveImageService.getSizes();
const lazyInitialized = ref(false);
const lazyLoaded = ref(false);
const hasSrcsetSupport = ref(true);
const loadEventFired = ref(true);
let observer: IntersectionObserver | undefined;

const activeAspectRatio = computed((): number => {
    if (typeof props.aspectRatio === 'number') {
        return props.aspectRatio;
    }

    // Else - object with aspect-ratio pr. breakpoint-def.

    // Find first active breakpoint-def
    const activeBpDef = Object.keys(props.aspectRatio).find(bpDef => breakpointsState.isBreakpointActive(bpDef));
    // Use active or default
    const result = activeBpDef ? props.aspectRatio[activeBpDef] : props.aspectRatio.default;
    if (!result) {
        throw new Error('Provide a breakpoint-definition for all cases or a default');
    }
    return result;
});

const normalizedBgColor = computed((): string => {
    return props.bgColor.replace('#', '');
});

const normalizedFormat = computed((): string => {
    // if jpeg file is uploaded to umbraco the extension could be "jpeg", force this to "jpg" as that is the format param that ImageSharp.web uses https://docs.sixlabors.com/articles/imagesharp.web/processingcommands.html#format
    return props.format === 'jpeg' ? 'jpg' : props.format;
});

const absoluteImageUrl = computed((): string => {
    return responsiveImageService.getBaseUrl() + props.imageUrl;
});

const heightFromWidth = (width: number): number => {
    return Math.round(width / activeAspectRatio.value);
};

const formatImageUrl = (width: number, height: number, quality?: number): string => {
    const firstSeparator = !props.imageUrl || props.imageUrl.indexOf('?') === -1 ? '?' : '&';
    const focal = props.focalPoint ? `&rxy=${props.focalPoint.left},${props.focalPoint.top}` : '';
    const bgColor = normalizedBgColor.value === 'transparent' ? '' : `&bgcolor=${normalizedBgColor.value}`;
    return `${absoluteImageUrl.value}${firstSeparator}format=${normalizedFormat.value}&width=${width}&height=${height}&quality=${quality || props.quality}&rmode=${props.mode}${focal}${bgColor}`;
};

const src = computed((): string => {
    if (!hasSrcsetSupport.value) {
        return fallbacksrc.value;
    }

    let lowSrc: string = '';

    if (props.lowsrc) {
        const lowSrcWidth = 100;
        const lowSrcQuality = 5;
        lowSrc = formatImageUrl(lowSrcWidth, heightFromWidth(lowSrcWidth), lowSrcQuality);
    } else {
        lowSrc = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
    }

    return lowSrc;
});

const fallbacksrc = computed((): string => {
    const width = sizes[sizes.length - 2]; // second largest
    return formatImageUrl(width, heightFromWidth(width));
});

const srcset = computed((): string => {
    let srcset: string = '';
    if (lazyInitialized.value && hasSrcsetSupport.value) {
        srcset = sizes
            .map(width => {
                return formatImageUrl(width, heightFromWidth(width)) + ` ${width}w`;
            })
            .join(', ');
    }
    return srcset;
});

const sortConfigBySize = (config: IResponsiveImageWidthOnScreenConfig) => {
    const sortedKeys = sortBy(keys(config), key => {
        return config[key];
    });

    return zipObject(
        sortedKeys,
        map(sortedKeys, function(key) {
            return config[key];
        })
    );
};

const sizesFromWidthOnScreen = computed((): string => {
    let sizes: string = '';

    if (typeof props.widthOnScreen === 'object') {
        sizes = map(sortConfigBySize(props.widthOnScreen), (width, screen) => {
            if (!breakpointService.getBreakpoint(screen)) {
                const errorMessage = `ResponsiveImage: Breakpoint ${screen} is not defined for imageUrl ${
                    absoluteImageUrl.value
                }`;
                loggingService.warn(errorMessage);
            }
            return `(min-width: ${breakpointService.getBreakpoint(screen).min}px) ${width}vw`;
        }).join(',');
        sizes += ', 100vw';
    } else {
        sizes = `${props.widthOnScreen}vw`;
    }

    return sizes;
});

const style = computed((): StyleValue => {
    if (props.aspectRatio === 0) {
        return {
            height: '100%',
            width: '100%'
        };
    }

    const padding = `${(100 / activeAspectRatio.value).toFixed(2)}%`;
    return { paddingTop: padding, background: `#${normalizedBgColor.value}` };
});

const handleLoadedAndLazyInitialized = (): void => {
    if (lazyInitialized.value && loadEventFired.value) {
        lazyLoaded.value = true;
    }
};

const handleLoad = (r: Event): void => {
    loadEventFired.value = true;
    handleLoadedAndLazyInitialized();

    emit('loaded');
};

const observe = (): void => {
    const options = { rootMargin: `${props.offset}px` };
    observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                lazyInitialized.value = true;
                handleLoadedAndLazyInitialized();
                unObserve();
            }
        });
    }, options);
    if (imageRef.value) {
        observer.observe(imageRef.value.parentElement as Element);
    }
};

const unObserve = (): void => {
    if (observer) {
        observer.disconnect();
    }
};

onMounted(() => {
    hasSrcsetSupport.value = imageRef.value ? 'sizes' in imageRef.value : true;
    observe();
});

onBeforeUnmount(() => {
    unObserve();
});
</script>

<style lang="less" scoped>
.responsive-image {
    position: relative;
    overflow: hidden;
    height: 0;
    display: block;

    img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        opacity: 0;
        transition: opacity 300ms ease-in;
    }

    &.responsive-image--loaded {
        img {
            opacity: 1;
        }
    }
}

.responsive-image--lowsrc > img {
    filter: blur(200px);
    transform: scale3d(2, 2, 2);
    transition: filter ease-in 100ms;
}

.responsive-image--lowsrc.responsive-image--loaded > img {
    filter: none;
    transform: none;
}
</style>
